/**
 * Languages (decision 46).
 *
 * A bot answers in one default language and, optionally, in more. A message
 * has one structure — the same buttons, media and pages — and each text in
 * it can be translated; anything left untranslated falls back to the
 * default. Which language a player gets comes from Telegram's
 * `language_code`, by its primary subtag: `pt-br` is `pt`, `zh-hans` is `zh`.
 */

export interface BotLanguages {
  default: string;
  extra: string[];
}

export const DEFAULT_LANGUAGES: BotLanguages = { default: "en", extra: [] };

/** A language code as stored: the primary subtag, lower case. */
export const LANGUAGE_CODE = /^[a-z]{2,3}$/;

/** The languages the panel offers, by their own names. */
export const LANGUAGE_NAMES: Record<string, string> = {
  en: "English",
  zh: "中文",
  ms: "Bahasa Melayu",
  id: "Bahasa Indonesia",
  th: "ไทย",
  vi: "Tiếng Việt",
  km: "ខ្មែរ",
  my: "မြန်မာ",
  lo: "ລາວ",
  ta: "தமிழ்",
  hi: "हिन्दी",
  bn: "বাংলা",
  ur: "اردو",
  ko: "한국어",
  ja: "日本語",
  ar: "العربية",
  fa: "فارسی",
  tr: "Türkçe",
  ru: "Русский",
  uk: "Українська",
  es: "Español",
  pt: "Português",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  nl: "Nederlands",
  pl: "Polski",
};

export function languageName(code: string): string {
  return LANGUAGE_NAMES[code] ?? code;
}

/** `pt-BR` → `pt`; null when there is no usable code. */
export function primaryLanguage(code: string | null | undefined): string | null {
  const primary = code?.split(/[-_]/)[0]?.toLowerCase() ?? "";
  return LANGUAGE_CODE.test(primary) ? primary : null;
}

/**
 * The translation a player with this Telegram language gets, or null for
 * the default texts.
 */
export function pickLanguage(
  languages: BotLanguages | null | undefined,
  code: string | null | undefined,
): string | null {
  const primary = primaryLanguage(code);
  if (!languages || !primary || primary === languages.default) return null;
  return languages.extra.includes(primary) ? primary : null;
}
